import {
    AssessmentUpdateDto,
    ClientIdentificationListDto,
    ClientIdentificationPanelDto,
    IdentityUpdateDto,
    ReassessDto,
    ReceiveDto,
    RelatedPartyRowUpdateDto,
    RevertReceiptDto,
    TierCode,
} from 'types/clientIdentification';
import { Contract } from 'types/contracts';
import { portalAPI } from './portalAPI';

type WithContractId<T> = T & { id: number };

const contractTags = (id: number) => [
    { type: 'Contract' as const, id: String(id) },
    'ContractsListItem' as const,
    'ContractsCount' as const,
];

const APIClientIdentificationsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'Contract',
            'ContractsListItem',
            'ContractsCount',
            'ClientIdentifications',
            'IdentificationPanel',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getClientIdentifications: builder.query<ClientIdentificationListDto, number>({
                query: (partyId) => `/v1/parties/${partyId}/compliance/client-identifications`,
                providesTags: (_result, _error, partyId) => [{ type: 'ClientIdentifications', id: partyId }],
            }),
            startClientIdentification: builder.mutation<
                { contractId: number },
                { partyId: number; tierCode: TierCode }
            >({
                query: ({ partyId, tierCode }) => ({
                    url: `/v1/parties/${partyId}/compliance/client-identifications`,
                    method: 'POST',
                    body: { tierCode },
                }),
                invalidatesTags: (_result, _error, { partyId }) => [
                    { type: 'ClientIdentifications', id: partyId },
                    'ContractsListItem',
                    'ContractsCount',
                ],
            }),
            getIdentificationPanel: builder.query<ClientIdentificationPanelDto, number>({
                query: (id) => `/v1/contracts/${id}/assessment/identification`,
                providesTags: (_result, _error, id) => [{ type: 'IdentificationPanel', id }],
            }),
            updateAssessment: builder.mutation<Contract, WithContractId<AssessmentUpdateDto>>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}/assessment`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => [
                    ...contractTags(id),
                    { type: 'IdentificationPanel', id },
                ],
            }),
            updateRelatedParties: builder.mutation<Contract, WithContractId<{ rows: RelatedPartyRowUpdateDto[] }>>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}/assessment/related-parties`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => [
                    ...contractTags(id),
                    { type: 'IdentificationPanel', id },
                ],
            }),
            updateIdentity: builder.mutation<ClientIdentificationPanelDto, WithContractId<IdentityUpdateDto>>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}/assessment/identity`,
                    method: 'PUT',
                    body,
                }),
                // The PUT answers with the panel as the GET does, so the cache takes it from the answer.
                async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                    try {
                        const { data } = await queryFulfilled;
                        dispatch(
                            APIClientIdentificationsSlice.util.upsertQueryData('getIdentificationPanel', id, data)
                        );
                    } catch {
                        // The form keeps what was typed; the failure is reported where every request's is.
                    }
                },
            }),
            receiveClientIdentification: builder.mutation<Contract, WithContractId<ReceiveDto>>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}/assessment/receive`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => [
                    ...contractTags(id),
                    { type: 'IdentificationPanel', id },
                    'ClientIdentifications',
                ],
            }),
            revertClientIdentificationReceipt: builder.mutation<Contract, WithContractId<RevertReceiptDto>>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}/assessment/revert-receipt`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => [
                    ...contractTags(id),
                    { type: 'IdentificationPanel', id },
                    'ClientIdentifications',
                ],
            }),
            reassessClientIdentification: builder.mutation<{ contractId: number }, WithContractId<ReassessDto>>({
                query: ({ id, ...body }) => ({
                    url: `/v1/contracts/${id}/assessment/reassess`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: (_result, _error, { id }) => [...contractTags(id), 'ClientIdentifications'],
            }),
        }),
        overrideExisting: false,
    });

/** The contract's own update invalidates no contract detail, so the page asks for its client identification again. */
export const invalidateClientIdentificationContract = (id: number) =>
    APIClientIdentificationsSlice.util.invalidateTags(contractTags(id));

/**
 * The generic contract delete invalidates no client identification list, so the client's Start button and, for a
 * deleted new version, the version it would have replaced are asked for again.
 */
export const invalidateDeletedClientIdentification = (supersedesContractId: number | null) =>
    APIClientIdentificationsSlice.util.invalidateTags([
        'ClientIdentifications',
        ...(supersedesContractId === null ? [] : contractTags(supersedesContractId)),
    ]);

export const {
    useGetClientIdentificationsQuery,
    useStartClientIdentificationMutation,
    useGetIdentificationPanelQuery,
    useUpdateAssessmentMutation,
    useUpdateRelatedPartiesMutation,
    useUpdateIdentityMutation,
    useReceiveClientIdentificationMutation,
    useRevertClientIdentificationReceiptMutation,
    useReassessClientIdentificationMutation,
} = APIClientIdentificationsSlice;
